import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import PeptideLanding from '@/pages/PeptideLanding';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import PaymentPage from '@/pages/PaymentPage';
import PublicPayment from '@/pages/PublicPayment';
import Financials from '@/pages/Financials';
import AdminDashboard from '@/pages/AdminDashboard';
import Products from '@/pages/Products';
import Cashiers from '@/pages/Cashiers';
import POS from '@/pages/POS';
import POSPayment from '@/pages/POSPayment';
import Checkout from '@/pages/Checkout';
import Widget from '@/pages/Widget';
import AdminManagement from '@/pages/AdminManagement';
import AgentDashboard from '@/pages/AgentDashboard';
import SuperAdminDashboard from '@/pages/SuperAdminDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (tokenValue, userData) => {
    localStorage.setItem('token', tokenValue);
    setToken(tokenValue);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  // Helper function to get the correct dashboard path based on user role
  const getDashboardPath = () => {
    if (!user) return '/auth';
    if (user.is_super_admin && user.role === 'super_admin') return '/super-admin';
    if (user.is_agent || user.role === 'agent') return '/agent';
    return '/dashboard';
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/peptides" element={<PeptideLanding />} />
          <Route path="/auth" element={user ? <Navigate to={getDashboardPath()} /> : <AuthPage />} />
          <Route path="/dashboard" element={user ? <Dashboard /> : <Navigate to="/auth" />} />
          <Route path="/financials" element={user ? <Financials /> : <Navigate to="/auth" />} />
          <Route path="/admin" element={user ? <AdminDashboard /> : <Navigate to="/auth" />} />
          <Route path="/admin/manage" element={user ? <AdminManagement /> : <Navigate to="/auth" />} />
          <Route path="/agent" element={user ? <AgentDashboard /> : <Navigate to="/auth" />} />
          <Route path="/super-admin" element={user ? <SuperAdminDashboard /> : <Navigate to="/auth" />} />
          <Route path="/products" element={user ? <Products /> : <Navigate to="/auth" />} />
          <Route path="/cashiers" element={user ? <Cashiers /> : <Navigate to="/auth" />} />
          <Route path="/widget" element={user ? <Widget /> : <Navigate to="/auth" />} />
          <Route path="/pos" element={user ? <POS /> : <Navigate to="/auth" />} />
          <Route path="/pos/payment/:invoiceId" element={user ? <POSPayment /> : <Navigate to="/auth" />} />
          <Route path="/checkout/:userId" element={<Checkout />} />
          <Route path="/pay" element={<PaymentPage />} />
          <Route path="/pay/:paymentId" element={<PublicPayment />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
export { API };